import { Bell, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('da-DK', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const CoachingModal = ({ open, onOpenChange, coaching, loading }) => {
  const tips = coaching?.tips || [];
  const risks = coaching?.risks || [];
  
  // Helper to render a tip based on its format
  const renderTip = (tip, idx) => {
    // Handle string format
    if (typeof tip === 'string') {
      return (
        <div key={idx} className="flex gap-3 p-3 bg-white border border-slate-200 rounded-lg">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">
            {idx + 1}
          </span>
          <p className="text-slate-700 text-sm">{tip}</p>
        </div>
      );
    }
    
    // Handle {tip: "..."} format
    if (tip.tip) {
      return (
        <div key={idx} className="flex gap-3 p-3 bg-white border border-slate-200 rounded-lg">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">
            {idx + 1}
          </span>
          <p className="text-slate-700 text-sm">{tip.tip}</p>
        </div>
      );
    }
    
    // Handle rich {title, do, say, avoid, why} format
    if (tip.title) {
      return (
        <div key={idx} className="p-4 bg-white border border-slate-200 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">
              {idx + 1}
            </span>
            <p className="font-medium text-slate-900">{tip.title}</p>
          </div>
          {tip.why && (
            <p className="text-slate-500 text-sm ml-8"><span className="font-medium">Hvorfor:</span> {tip.why}</p>
          )}
          {tip.do && (
            <p className="text-emerald-700 text-sm ml-8"><span className="font-medium">Gør:</span> {tip.do}</p>
          )}
          {tip.say && (
            <p className="text-blue-700 text-sm ml-8"><span className="font-medium">Sig:</span> {tip.say}</p>
          )}
          {tip.avoid && (
            <p className="text-red-600 text-sm ml-8"><span className="font-medium">Undgå:</span> {tip.avoid}</p>
          )}
        </div>
      );
    }
    
    // Fallback - try to stringify
    return (
      <div key={idx} className="flex gap-3 p-3 bg-white border border-slate-200 rounded-lg">
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">
          {idx + 1}
        </span>
        <p className="text-slate-700 text-sm">{JSON.stringify(tip)}</p>
      </div>
    );
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-600" />
            Coaching til sælger
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : coaching ? (
          <div className="overflow-auto flex-1 space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Kunde:</span>
                  <p className="font-medium text-slate-900">{coaching.customer_name || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Konsulent:</span>
                  <p className="font-medium text-slate-900">{coaching.consultant_name || '-'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Kilde:</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    coaching.coaching_source === 'survey' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {coaching.coaching_source === 'survey' ? 'Fra survey' : 'Standard'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Genereret:</span>
                  <p className="font-medium text-slate-900">{formatDate(coaching.generated_at)}</p>
                </div>
              </div>
            </div>

            {coaching.opening_line && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-800 mb-1">Åbningslinje</p>
                <p className="text-slate-700">{coaching.opening_line}</p>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Coaching tips ({tips.length})</p>
              <div className="space-y-2">
                {tips.length > 0 ? tips.map((tip, idx) => renderTip(tip, idx)) : (
                  <p className="text-slate-400 text-sm py-4 text-center">Ingen tips genereret</p>
                )}
              </div>
            </div>

            {risks.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Risici at være opmærksom på</p>
                <div className="space-y-2">
                  {risks.map((risk, idx) => (
                    <div key={idx} className="flex gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <span className="text-red-500 text-lg">⚠️</span>
                      <p className="text-slate-700 text-sm">
                        {typeof risk === 'string' ? risk : (risk.risk || risk.text || JSON.stringify(risk))}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8 text-center text-slate-400">
            Kunne ikke hente coaching data
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CoachingModal;
